import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const location = useLocation();

  const primaryNavItems = [
    { path: '/fleet-dashboard', label: 'Fleet Overview', icon: 'Ship' },
    { path: '/voyage-planning', label: 'Voyage Planning', icon: 'Route' },
    { path: '/emissions-calculator', label: 'Emissions', icon: 'Calculator' },
    { path: '/eua-trading-hub', label: 'EUA Trading', icon: 'TrendingUp' },
    { path: '/compliance-reports', label: 'Compliance', icon: 'FileText' }
  ];

  const secondaryNavItems = [
    { path: '/vessel-performance', label: 'Vessel Performance', icon: 'BarChart3' },
    { path: '/user-management', label: 'User Management', icon: 'Users' }
  ];

  const isActivePath = (path) => location?.pathname === path;

  const handleMoreMenuToggle = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  const handleMoreMenuClose = () => {
    setIsMoreMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-1000 bg-card border-b border-border shadow-maritime-sm">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo Section */}
        <div className="flex items-center">
          <Link to="/fleet-dashboard" className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
              <Icon name="Anchor" size={24} color="white" strokeWidth={2} />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground font-heading">
                Carbon Cost Router
              </span>
              <span className="text-xs text-muted-foreground font-caption">
                Maritime Carbon Intelligence
              </span>
            </div>
          </Link>
        </div>

        {/* Primary Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {primaryNavItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-maritime ${
                isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground shadow-maritime-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={item?.icon} size={16} strokeWidth={2} />
              <span>{item?.label}</span>
            </Link>
          ))}
          
          {/* More Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMoreMenuToggle}
              className={`flex items-center space-x-2 ${
                isMoreMenuOpen ? 'bg-muted' : ''
              }`}
            >
              <Icon name="MoreHorizontal" size={16} strokeWidth={2} />
              <span>More</span>
              <Icon 
                name="ChevronDown" 
                size={14} 
                strokeWidth={2}
                className={`transition-transform duration-200 ${
                  isMoreMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </Button>
            
            {isMoreMenuOpen && (
              <>
                <div 
                  className="fixed inset-0 z-1000" 
                  onClick={handleMoreMenuClose}
                />
                <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-md shadow-maritime-lg z-1100 animate-fade-in">
                  <div className="py-2">
                    {secondaryNavItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        onClick={handleMoreMenuClose}
                        className={`flex items-center space-x-3 px-4 py-2 text-sm transition-maritime ${
                          isActivePath(item?.path)
                            ? 'bg-accent text-accent-foreground'
                            : 'text-popover-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={16} strokeWidth={2} />
                        <span>{item?.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMoreMenuToggle}
            className="p-2"
          >
            <Icon name="Menu" size={20} strokeWidth={2} />
          </Button>
        </div>

        {/* User Actions */}
        <div className="hidden lg:flex items-center space-x-3">
          <Button variant="ghost" size="sm" className="p-2">
            <Icon name="Bell" size={18} strokeWidth={2} />
          </Button>
          <Button variant="ghost" size="sm" className="p-2">
            <Icon name="Settings" size={18} strokeWidth={2} />
          </Button>
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <Icon name="User" size={16} color="white" strokeWidth={2} />
          </div>
        </div>
      </div>
      {/* Mobile Navigation Menu */}
      {isMoreMenuOpen && (
        <div className="lg:hidden border-t border-border bg-card">
          <div className="px-4 py-3 space-y-1">
            {[...primaryNavItems, ...secondaryNavItems]?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={handleMoreMenuClose}
                className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-maritime ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={16} strokeWidth={2} />
                <span>{item?.label}</span>
              </Link>
            ))}
            
            <div className="pt-3 mt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={16} color="white" strokeWidth={2} />
                  </div>
                  <span className="text-sm font-medium text-foreground">User Profile</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm" className="p-2">
                    <Icon name="Bell" size={16} strokeWidth={2} />
                  </Button>
                  <Button variant="ghost" size="sm" className="p-2">
                    <Icon name="Settings" size={16} strokeWidth={2} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;